import { useEffect, useState } from "react";
import { useUrlSearch } from "@/lib/useUrlSearch";
import { api, formatApiError, inr, fmtDate, API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, FileText, Trash2, PackageCheck, ReceiptText, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import PageHeader from "@/components/PageHeader";
import { usePager, Pager } from "@/components/Pager";
import { StatusPill } from "@/pages/Quotations";
import { ProductReadOnlyCards } from "@/components/ProductReadOnlyCards";

const emptyForm = {
  supplier_name: "", supplier_gstin: "", supplier_address: "", supplier_state: "",
  supplier_email: "", supplier_phone: "", transport: "", reference: "", date: "", delivery_date: "",
  sales_order_id: "",
  items: [], freight: 0, terms: "",
};

export default function PurchaseOrders() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [orders, setOrders] = useState([]);

  const load = () => Promise.all([
    api.get("/purchase-orders").then((r) => setList(r.data)),
    api.get("/sales-orders").then((r) => setOrders(r.data)),
  ]);
  const [poTab, setPoTab] = useState("active");
  const [poSearch, setPoSearch] = useUrlSearch();
  const poNorm = poSearch.trim().toLowerCase();
  const searchedPo = poNorm
    ? list.filter((p) => [p.number, p.supplier_name, p.customer_name, p.sales_order_number]
        .some((v) => String(v || "").toLowerCase().includes(poNorm)))
    : list;
  const shownPo = searchedPo.filter((p) => (poTab === "closed" ? p.status !== "Open" : p.status === "Open"));
  useEffect(() => {
    if (!poNorm) return;
    const open = searchedPo.filter((p) => p.status === "Open").length;
    const closed = searchedPo.length - open;
    if (poTab === "active" && open === 0 && closed > 0) setPoTab("closed");
    if (poTab === "closed" && closed === 0 && open > 0) setPoTab("active");
  }, [poNorm, list]);
  const pager = usePager(shownPo);
  useEffect(() => { load(); }, []);

  const totals = form.items.reduce((acc, it) => {
    const sub = (+it.quantity || 0) * (+it.unit_price || 0);
    acc.sub += sub; acc.gst += sub * (+it.gst || 0) / 100; return acc;
  }, { sub: 0, gst: 0 });
  const freightGst = (+form.freight || 0) * 0.18;
  const total = totals.sub + totals.gst + freightGst + (+form.freight || 0);

  const save = async () => {
    if (!form.supplier_name.trim()) { toast.error("Supplier name is required"); return; }
    try {
      const body = { ...form, freight: +form.freight,
        items: form.items.map((i) => ({ ...i, quantity: +i.quantity, unit_price: +i.unit_price, gst: +i.gst })) };
      if (!body.date) delete body.date;
      if (!body.delivery_date) delete body.delivery_date;
      if (editing) await api.put(`/purchase-orders/${editing}`, body);
      else await api.post("/purchase-orders", body);
      toast.success("Purchase Order saved");
      setOpen(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const edit = (p) => {
    setEditing(p.id);
    setForm({ ...emptyForm, ...p, date: p.date ? String(p.date).slice(0, 10) : "",
              delivery_date: p.delivery_date ? p.delivery_date.slice(0, 10) : "",
              items: p.items?.length ? p.items : [] });
    setOpen(true);
  };
  const del = async (id) => { if (!window.confirm("Delete PO?")) return; await api.delete(`/purchase-orders/${id}`); load(); };

  const [billFor, setBillFor] = useState(null);
  const [bill, setBill] = useState({ number: "", date: "", amount: "" });
  const [billFile, setBillFile] = useState(null);
  const [billBusy, setBillBusy] = useState(false);
  const openBill = (p) => {
    setBill({ number: "", date: "", amount: p.total || "" });
    setBillFile(null);
    setBillFor(p);
  };
  const markReceived = async (p) => {
    try {
      await api.patch(`/purchase-orders/${p.id}/status`, null, { params: { status: "Received" } });
      toast.success(`PO ${p.number} marked Received`);
      load();
      openBill(p);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveBill = async () => {
    if (!bill.number.trim()) { toast.error("Enter the vendor's bill number"); return; }
    setBillBusy(true);
    const tid = billFile ? toast.loading("Reading bill with AI & checking the amount…") : null;
    try {
      const fd = new FormData();
      fd.append("bill_number", bill.number.trim());
      fd.append("amount", +bill.amount || 0);
      fd.append("bill_date", bill.date || "");
      if (billFile) fd.append("file", billFile);
      const r = await api.post(`/purchase-orders/${billFor.id}/bill`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const ai = r.data.ai_check;
      if (ai?.match === false) toast.warning(ai.note, { id: tid, duration: 12000 });
      else if (ai?.match === true) toast.success(ai.note, { id: tid, duration: 8000 });
      else toast.success(`Bill ${bill.number} recorded${r.data.merged ? " (combined with existing multi-PO bill)" : ""}${ai?.note ? ` — ${ai.note}` : ""}`, { id: tid, duration: 8000 });
      setBillFor(null); load();
    } catch (e) { toast.error(formatApiError(e), { id: tid }); }
    setBillBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="purchase-orders-page">
      <PageHeader title="Purchase Orders" subtitle="POs issued to raw-material suppliers"
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditing(null); setForm({ ...emptyForm, items: [] }); setOpen(true); }}
                      className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="po-new">
                <Plus size={14} className="mr-1" /> New PO
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[96vw] sm:max-w-[96vw] h-[94vh] max-h-[94vh] overflow-y-auto content-start lg:left-[calc(16rem+10px)] lg:translate-x-0 lg:top-[10px] lg:translate-y-0 lg:w-[calc(100vw-16rem-20px)] lg:max-w-[calc(100vw-16rem-20px)] lg:h-[calc(100vh-20px)] lg:max-h-[calc(100vh-20px)] lg:rounded-xl shadow-2xl dialog-slide-right">
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Purchase Order</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <F l="Supplier Name *" v={form.supplier_name} on={(v) => setForm({ ...form, supplier_name: v })} tid="po-supplier" />
                  <F l="Supplier GSTIN" v={form.supplier_gstin} on={(v) => setForm({ ...form, supplier_gstin: v.toUpperCase() })} tid="po-gstin" />
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Supplier Address</Label>
                    <Textarea value={form.supplier_address} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} rows={2} className="mt-1.5" data-testid="po-address" />
                  </div>
                  <F l="Supplier State" v={form.supplier_state} on={(v) => setForm({ ...form, supplier_state: v })} />
                  <F l="Supplier Email" v={form.supplier_email} on={(v) => setForm({ ...form, supplier_email: v })} />
                  <F l="Supplier Phone" v={form.supplier_phone} on={(v) => setForm({ ...form, supplier_phone: v })} />
                  <F l="Transport" v={form.transport} on={(v) => setForm({ ...form, transport: v })} />
                  <F l="Reference" v={form.reference} on={(v) => setForm({ ...form, reference: v })} />
                  <F l="PO Date" v={form.date} on={(v) => setForm({ ...form, date: v })} type="date" />
                  <F l="Delivery Date" v={form.delivery_date} on={(v) => setForm({ ...form, delivery_date: v })} type="date" />
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Against Sales Order (optional — shows customer on the PO list)</Label>
                    <Select value={form.sales_order_id || "none"} onValueChange={(v) => setForm({ ...form, sales_order_id: v === "none" ? "" : v })}>
                      <SelectTrigger className="mt-1.5 w-full" data-testid="po-so"><SelectValue placeholder="No sales order" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No sales order</SelectItem>
                        {orders.filter((o) => o.status !== "Cancelled" && o.status !== "Closed").map((o) => (
                          <SelectItem key={o.id} value={o.id}>
                            <span className="font-mono text-xs mr-1">{o.number}</span>
                            <span className="text-slate-600">· {o.customer_snapshot?.company_name}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <ProductReadOnlyCards items={form.items} note="cascaded from sales order — read-only" testPrefix="po-item" />

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-slate-500">Freight (INR)</Label>
                    <Input type="number" value={form.freight} onChange={(e) => setForm({ ...form, freight: e.target.value })} className="mt-1.5" data-testid="po-freight" />
                  </div>
                  <div className="col-span-2 flex flex-col items-end justify-end text-sm">
                    <div className="w-full max-w-xs space-y-1 text-slate-600">
                      <div className="flex justify-between"><span>Subtotal</span><span className="tabular-nums">{inr(totals.sub)}</span></div>
                      <div className="flex justify-between"><span>GST{+form.freight > 0 ? " (incl. 18% on freight)" : ""}</span><span className="tabular-nums">{inr(totals.gst + freightGst)}</span></div>
                      <div className="flex justify-between font-bold text-slate-900 border-t border-slate-300 pt-1"><span>Total</span><span className="tabular-nums">{inr(total)}</span></div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-widest text-slate-500">Terms & Conditions</Label>
                  <Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} rows={2} className="mt-1.5" data-testid="po-terms" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="po-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="po-tabs">
          {[["active", "Active"], ["closed", "Closed"]].map(([v, l]) => (
            <button key={v} onClick={() => setPoTab(v)} data-testid={`po-tab-${v}`}
              className={`px-4 py-1.5 text-sm rounded-md transition-colors duration-200 ${poTab === v ? "bg-white shadow-sm font-semibold text-[#3B82F6]" : "text-slate-500 hover:text-slate-700"}`}>
              {l}
            </button>
          ))}
        </div>
        <Input value={poSearch} onChange={(e) => setPoSearch(e.target.value)} placeholder="Search PO no., supplier, customer…"
               className="h-9 w-64 bg-white" data-testid="po-search" />
      </div>

      <div className="border border-slate-100 rounded-xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Number</th>
              <th className="text-left px-4 py-2.5">Supplier</th>
              <th className="text-left px-4 py-2.5 hidden sm:table-cell">Customer</th>
              <th className="text-left px-4 py-2.5 hidden md:table-cell">Date</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageItems.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/70" data-testid={`po-row-${p.id}`}>
                <td className="px-4 py-3 font-mono text-slate-700">{p.number}</td>
                <td className="px-4 py-3">
                  {p.supplier_name}
                  {p.sales_order_number && <div className="text-[11px] font-mono text-slate-500">Against {p.sales_order_number}</div>}
                </td>
                <td className="px-4 py-3 hidden sm:table-cell text-slate-600" data-testid={`po-customer-${p.id}`}>{p.customer_name || "—"}</td>
                <td className="px-4 py-3 hidden md:table-cell">{fmtDate(p.date)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{inr(p.total)}</td>
                <td className="px-4 py-3">
                  <StatusPill status={p.status} />
                  {p.status === "Received" && p.bill_number && (
                    <div className="text-[11px] text-emerald-700 mt-1 font-mono" data-testid={`po-bill-chip-${p.id}`}>Bill ✓ {p.bill_number}</div>
                  )}
                  {p.status === "Received" && !p.bill_number && (
                    <button onClick={() => openBill(p)} className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5 font-semibold hover:bg-violet-200 transition-colors duration-150" data-testid={`po-add-bill-${p.id}`}>
                      <ReceiptText size={10} /> Add Bill
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <a href={`${API_URL}/purchase-orders/${p.id}/pdf`} target="_blank" rel="noreferrer"
                       className="p-1.5 text-slate-500 hover:text-blue-600" title="PDF" data-testid={`po-pdf-${p.id}`}><FileText size={13} /></a>
                    <button onClick={() => edit(p)} className="p-1.5 text-slate-500 hover:text-blue-600 text-xs" data-testid={`po-edit-${p.id}`}>Edit</button>
                    {p.status === "Open" && <button onClick={() => markReceived(p)} className="p-1.5 text-emerald-700 hover:text-emerald-900 rounded hover:bg-emerald-50 transition-colors duration-150" title="Mark Received" data-testid={`po-mark-received-${p.id}`}><PackageCheck size={13} /></button>}
                    <button onClick={() => del(p.id)} className="p-1.5 text-slate-500 hover:text-rose-600" data-testid={`po-del-${p.id}`}><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {shownPo.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-sm text-slate-500">{poTab === "closed" ? "No closed purchase orders yet." : "No active purchase orders."}</td></tr>}
          </tbody>
        </table>
        <Pager pager={pager} />
      </div>

      <Dialog open={!!billFor} onOpenChange={(v) => { if (!v) setBillFor(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Vendor Bill — {billFor?.number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500 -mt-1">
              {billFor?.supplier_name} · PO total {inr(billFor?.total)}. One bill covering multiple POs?
              Enter the <b>same bill number</b> on each PO — they'll combine into one bill automatically.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Bill Number *</Label>
                <Input value={bill.number} onChange={(e) => setBill({ ...bill, number: e.target.value })}
                       placeholder="Vendor's invoice/bill no." className="mt-1.5" data-testid="po-bill-number" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Bill Date</Label>
                <DatePicker value={bill.date} onChange={(v) => setBill({ ...bill, date: v })} className="mt-1.5" testId="po-bill-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500">Amount (INR)</Label>
                <Input type="number" value={bill.amount} onChange={(e) => setBill({ ...bill, amount: e.target.value })} className="mt-1.5" data-testid="po-bill-amount" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest text-slate-500">Bill File (optional — AI verifies the amount)</Label>
                <input type="file" accept=".pdf,image/*" className="hidden" id="po-bill-file-input"
                       onChange={(e) => setBillFile(e.target.files?.[0] || null)} data-testid="po-bill-file-input" />
                <button type="button" onClick={() => document.getElementById("po-bill-file-input").click()}
                        className="mt-1.5 inline-flex items-center gap-1.5 text-xs border border-slate-200 rounded-md px-2.5 h-9 text-slate-600 hover:border-slate-400 transition-colors duration-150 w-full"
                        data-testid="po-bill-upload-btn">
                  <Paperclip size={12} className="flex-none" />
                  <span className="truncate">{billFile ? billFile.name : "Upload bill PDF / photo"}</span>
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillFor(null)} data-testid="po-bill-later">Enter Later</Button>
            <Button onClick={saveBill} disabled={billBusy} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="po-bill-save">
              {billBusy ? "Saving…" : "Save Bill"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ l, v, on, tid, type = "text" }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-slate-500">{l}</Label>
      {type === "date"
        ? <DatePicker value={v} onChange={on} className="mt-1.5" testId={tid} />
        : <Input value={v} onChange={(e) => on(e.target.value)} className="mt-1.5" data-testid={tid} type={type} />}
    </div>
  );
}
